"use client";
import { useState, useEffect, useRef, JSX } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useAppSelector,
  useSaveStateToLocalStorageOnChange,
  useSetInitialStore,
} from "lib/redux/hooks";
import {
  ShowForm,
  selectFormsOrder,
  setCurrentSection,
  selectCurrentSection,
  toggleProjectRecommendations,
  selectTemplate,
  selectSectionTitles,
  toggleReset,
  setFormHeadingToDefault,
  selectLeftRightFormOrder,
  selectSettings,
  setLeftRightFormOrder,
} from "lib/redux/settingsSlice";
import {
  resetResume,
  resetSection,
  selectResume,
  selectLeftRightResumeFormOrder,
} from "lib/redux/resumeSlice";
import { ProfileForm } from "components/ResumeForm/ProfileForm";
import { WorkExperiencesForm } from "components/ResumeForm/WorkExperiencesForm";
import { EducationsForm } from "components/ResumeForm/EducationsForm";
import { ProjectsForm } from "components/ResumeForm/ProjectsForm";
import { SkillsForm } from "components/ResumeForm/SkillsForm";
import { CertificatesForm } from "components/ResumeForm/CertificatesForm";
import { LanguagesForm } from "components/ResumeForm/LanguagesForm";
import { AwardsForm } from "components/ResumeForm/AwardsForm";
import { ThemeForm } from "components/ResumeForm/ThemeForm";
import { CustomForm } from "components/ResumeForm/CustomForm";
import { TemplateForm } from "components/TemplateForm";
import { cx } from "lib/cx";
import Sidebar from "./Sidebar/Sidebar";
import { usePostHog } from "posthog-js/react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import MobileSidebar from "./Sidebar/MobileSidebar";
import CustomIcon from "components/CustomIcon";
import { useTemplateComponents } from "components/Resume/templateUtils";
import UpgradeToProCTAForm from "../Payment/UpgradeToProCTAForm";
import {
  selectIsOutOfCreditsModalOpen,
  selectIsPremiumUser,
} from "lib/redux/uiSlice";
// import DebugDownloadModalAd from "components/Ads/DownloadModalAd";
// import SidebarAd from "components/Ads/BuilderSideBarAd";
// import DebugSidebarAd from "components/Ads/BuilderSideBarAd";
// import SidebarAd1560 from "components/Ads/BuilderSideBarAd1560";
import { PremiumCard } from "components/PremiumCard";
import Link from "next/link";
// import DebugDownloadModalAd from "components/Ads/DownloadModalAd";

const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
  workExperiences: WorkExperiencesForm,
  educations: EducationsForm,
  projects: ProjectsForm,
  skills: SkillsForm,
  certifications: CertificatesForm,
  languages: LanguagesForm,
  awards: AwardsForm,
  custom: CustomForm,
};

export const ResumeForm = () => {
  useSetInitialStore();
  useSaveStateToLocalStorageOnChange();

  const formsOrder = useAppSelector(selectFormsOrder);
  const [isHover, setIsHover] = useState(false);
  const sectionTitles = useAppSelector(selectSectionTitles);
  const currentSection = useSelector(selectCurrentSection);
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const currentIndex = sectionTitles.findIndex(
    (section) => section.id === currentSection
  );
  const template = useAppSelector(selectTemplate);
  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);
  const { templateList } = useTemplateComponents(resume, settings);
  const leftRightFormOrderInResume = useAppSelector(
    selectLeftRightResumeFormOrder
  );
  const isOutOfCreditsModalOpen = useAppSelector(selectIsOutOfCreditsModalOpen);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);
  const divHeightRef = useRef<HTMLDivElement>(null);
  const [initialTemplateId, setInitialTemplateId] = useState<number>(1);
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(true); // default to logged out for SSR

  useEffect(() => {
    // Check localStorage only in browser (after mount)
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("access-token");
      setIsLoggedOut(!token);
    }
  }, []);

  // Set the left right form order based on the selected template
  useEffect(() => {
    const selectedTemplate = templateList.find((t) => t.id === template);
    if (selectedTemplate?.formsOrder) {
      if (
        resume.id &&
        resume.templateId === initialTemplateId &&
        leftRightFormOrderInResume.left.length !== 0 &&
        leftRightFormOrderInResume.right.length !== 0
      ) {
        dispatch(setLeftRightFormOrder(leftRightFormOrderInResume as any));
      } else {
        dispatch(setLeftRightFormOrder(selectedTemplate.formsOrder));
      }
    }
  }, [template]);

  const projectRecommendationsClicked = () => {
    posthog.capture("project_recommendations_clicked");
    toggleProjectRecommendationsState();
  };

  const handleSectionReset = () => {
    dispatch(toggleReset());
    dispatch(resetSection({ section: currentSection }));
    dispatch(setFormHeadingToDefault({ field: [currentSection as ShowForm] }));
  };

  const handleReset = () => {
    dispatch(resetResume());
  };

  const toggleProjectRecommendationsState = () => {
    dispatch(toggleProjectRecommendations()); // Dispatching the action to toggle
  };

  const handlePrev = () => {
    const prevSection = sectionTitles[currentIndex - 1];
    if (prevSection) {
      dispatch(setCurrentSection(prevSection.id));
    }
  };

  const handleNext = () => {
    const nextSection = sectionTitles[currentIndex + 1];
    if (nextSection) {
      dispatch(setCurrentSection(nextSection.id));
    }
  };

  const renderSection = () => {
    if (currentSection === "personalDetails") {
      return <ProfileForm />;
    }
    if (currentSection === "resumeSettings") {
      return <ThemeForm />;
    }
    if (currentSection === "templates") {
      return <TemplateForm />;
    }
    const formKey = formsOrder.find((form) => form === currentSection);
    if (formKey) {
      const Component = formTypeToComponent[formKey];
      return <Component />;
    }
    return null;
  };

  return (
    <div
      className={cx(
        // "flex h-[calc(100vh-var(--top-nav-bar-height))]",
        "flex h-full flex-col lg:flex-row lg:overflow-hidden",
        isHover && "scrollbar-thumb-gray-200"
      )}
      onMouseOver={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="hidden lg:flex lg:flex-shrink-0 lg:flex-col">
        <Sidebar />

        {/* Only show premium card for non-premium or logged-out users */}
        {(!isPremiumUser || isLoggedOut) && (
          <>
            {/* <div className="mt-2 flex [@media(min-width:1559px)]:hidden w-full justify-center px-2">
              <SidebarAd
                adClient="ca-pub-8612844749010589"
                adSlot="4334565515"
                firstDelay={30000}
                refreshInterval={30000}
                adTest={false}
                onlyWhenVisible={true}
              />
            </div>
            <div className="hidden mt-2 [@media(min-width:1559px)]:flex w-full justify-center px-2">
              <SidebarAd1560
                adClient="ca-pub-8612844749010589"
                adSlot="1319188234"
                firstDelay={30000}
                refreshInterval={30000}
                adTest={false}
                onlyWhenVisible={true}
              />
            </div> */}
            <div className="flex w-full justify-center">
              <Link href="/premium-subscription">
                <PremiumCard />
              </Link>
            </div>
          </>
        )}
      </div>
      <div className="my-3 flex items-center justify-between lg:hidden">
        <div className="z-10 w-1/2">
          <MobileSidebar />
        </div>
        {currentSection !== "templates" &&
          currentSection !== "resumeSettings" && (
            <button
              className="btn-gradient items-center gap-2 rounded-[38px] px-2 py-1 text-[10px]"
              onClick={handleSectionReset}
            >
              <div className="">
                <CustomIcon iconName="reset" stroke="white" />
              </div>
              Reset
            </button>
          )}
      </div>
      <div
        className={cx(
          "progress-scroll flex-1",
          "pb-[var(--resume-padding)] lg:mt-2.5 lg:px-[var(--resume-padding)]",
          "overflow-y-scroll lg:scrollbar",
          "relative"
        )}
        ref={divHeightRef}
      >
        {renderSection()}
        {
          // this modal will open when the user is not a premium user and has exhausted the free credits
          !isPremiumUser && isOutOfCreditsModalOpen && (
            <UpgradeToProCTAForm
              height={
                divHeightRef.current?.scrollHeight
                  ? `${divHeightRef.current.scrollHeight}px`
                  : "auto" // Fallback value if ref is undefined
              }
            />
          )
        }
        <br />
        {currentSection !== "templates" &&
          currentSection !== "resumeSettings" && (
            <div className="flex justify-end gap-4">
              {currentIndex !== 0 && (
                <button
                  className={cx(
                    "rounded-full bg-white p-2 text-sm shadow-md",
                    "flex items-center gap-1",
                    "hover:btn-gradient hover:border-none"
                  )}
                  onClick={handlePrev}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
              )}
              {currentIndex !== sectionTitles.length - 1 && (
                <button
                  className={cx(
                    "rounded-full bg-white p-2 text-sm shadow-md",
                    "flex items-center gap-1",
                    "hover:btn-gradient hover:border-none"
                  )}
                  onClick={handleNext}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
      </div>
    </div>
  );
};
